import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    User,
    Mail,
    Phone,
    Building,
    Shield,
    Edit,
    Save,
    X,
    CheckCircle,
    AlertCircle,
    Loader2,
    Calendar,
    Clock,
    Award,
    Settings,
    LogOut,
    UserCheck,
    Fingerprint,
    Globe,
    MapPin,
    Briefcase,
    CreditCard
} from 'lucide-react';
import api from '../../services/api';

export default function ProfilePage() {
    const { user } = useSelector((state) => state.auth);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({
        full_name: user?.full_name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        company_name: user?.company_name || '',
        kra_pin: user?.kra_pin || '',
        pin: user?.pin || '',
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [memberSince, setMemberSince] = useState('January 2024');
    const [lastLogin, setLastLogin] = useState(new Date());

    useEffect(() => {
        // Format member since date
        if (user?.created_at) {
            const date = new Date(user.created_at);
            setMemberSince(date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }));
        }
        // Get last login from localStorage or use current time
        const savedLastLogin = localStorage.getItem('lastLogin');
        if (savedLastLogin) {
            setLastLogin(new Date(savedLastLogin));
        }
    }, [user]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            await api.put('/users/me', form);
            setSuccess('Profile updated successfully.');
            setEditing(false);
            // Update user in localStorage or state
            const updatedUser = { ...user, ...form };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Update failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setEditing(false);
        setForm({
            full_name: user?.full_name || '',
            email: user?.email || '',
            phone: user?.phone || '',
            company_name: user?.company_name || '',
            kra_pin: user?.kra_pin || '',
            pin: user?.pin || '',
        });
        setError('');
        setSuccess('');
    };

    const initials = user?.full_name
        ?.split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2) || 'U';

    const getRoleColor = (role) => {
        switch (role?.toLowerCase()) {
            case 'admin': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
            case 'underwriter': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
            case 'agent': return 'bg-[#3a663f]/10 dark:bg-[#3a663f]/20 text-[#3a663f] dark:text-green-400';
            case 'broker': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400';
            case 'direct_client': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
            default: return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400';
        }
    };

    const getRoleDisplay = (role) => {
        return role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'User';
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Header */}
                <div className="relative bg-gradient-to-br from-[#1e3b29] via-[#3a663f] to-[#4d8c54] rounded-2xl p-8 md:p-10 text-white overflow-hidden shadow-2xl">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
                    <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/10 rounded-full translate-y-1/3 -translate-x-1/3" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl" />

                    <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        <div className="flex items-center gap-4">
                            <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white text-3xl font-bold shadow-lg border-2 border-white/30">
                                {initials}
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <UserCheck className="w-4 h-4 text-green-300" />
                                    <span className="text-green-200 text-xs uppercase tracking-widest font-semibold">Profile</span>
                                </div>
                                <h1 className="text-2xl md:text-3xl font-extrabold">
                                    {user?.full_name || 'User'}
                                </h1>
                                <p className="text-green-100/80 text-sm flex items-center gap-2">
                                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user?.role)}`}>
                                        {getRoleDisplay(user?.role)}
                                    </span>
                                    <span>�</span>
                                    <span>Active</span>
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg text-sm flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse" />
                                <span className="opacity-60">Status:</span>
                                <span className="font-medium">Online</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Success/Error Messages */}
                {success && (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 p-4 rounded-xl flex items-start gap-3 animate-fadeIn">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm flex-1">{success}</span>
                        <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}
                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 p-4 rounded-xl flex items-start gap-3 animate-fadeIn">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm flex-1">{error}</span>
                        <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Main Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Profile Card */}
                    <div className="lg:col-span-2">
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                            <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <User className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                    <h2 className="font-bold text-gray-800 dark:text-white">Personal Information</h2>
                                </div>
                                {!editing && (
                                    <button
                                        onClick={() => setEditing(true)}
                                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-[#3a663f] dark:text-green-400 hover:bg-[#3a663f]/10 dark:hover:bg-[#3a663f]/20 rounded-lg font-medium transition-colors"
                                    >
                                        <Edit className="w-4 h-4" />
                                        Edit Profile
                                    </button>
                                )}
                            </div>

                            <div className="p-6">
                                {editing ? (
                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                                                Full Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="full_name"
                                                value={form.full_name}
                                                onChange={handleChange}
                                                className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all hover:shadow-sm"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                                                Email <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={form.email}
                                                onChange={handleChange}
                                                className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all hover:shadow-sm"
                                                required
                                                disabled
                                            />
                                            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">Email cannot be changed. Contact support for assistance.</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                                                Phone
                                            </label>
                                            <input
                                                type="text"
                                                name="phone"
                                                value={form.phone}
                                                onChange={handleChange}
                                                placeholder="e.g., +254 712 345 678"
                                                className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all hover:shadow-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                                                Company Name
                                            </label>
                                            <input
                                                type="text"
                                                name="company_name"
                                                value={form.company_name}
                                                onChange={handleChange}
                                                placeholder="e.g., ABC Insurance Ltd"
                                                className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all hover:shadow-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                                                KRA PIN
                                            </label>
                                            <input
                                                type="text"
                                                name="kra_pin"
                                                value={form.kra_pin}
                                                onChange={handleChange}
                                                placeholder="e.g., A123456789B"
                                                className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all hover:shadow-sm font-mono uppercase"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                                                PIN
                                            </label>
                                            <input
                                                type="text"
                                                name="pin"
                                                value={form.pin}
                                                onChange={handleChange}
                                                placeholder="e.g., 123456"
                                                className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all hover:shadow-sm"
                                            />
                                        </div>
                                        <div className="flex gap-3 pt-2">
                                            <button
                                                type="button"
                                                onClick={handleCancel}
                                                className="flex-1 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl text-gray-600 dark:text-slate-400 font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-2"
                                            >
                                                <X className="w-4 h-4" />
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={loading}
                                                className="flex-1 py-2.5 bg-[#3a663f] hover:bg-[#2d5231] text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                            >
                                                {loading ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                        Saving...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Save className="w-4 h-4" />
                                                        Save Changes
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <InfoItem icon={User} label="Full Name" value={user?.full_name} />
                                            <InfoItem icon={Mail} label="Email" value={user?.email} />
                                            <InfoItem icon={Phone} label="Phone" value={user?.phone || 'Not provided'} />
                                            <InfoItem icon={Building} label="Company" value={user?.company_name || 'Not provided'} />
                                            <InfoItem icon={Fingerprint} label="KRA PIN" value={user?.kra_pin || 'Not provided'} />
                                            <InfoItem icon={CreditCard} label="PIN" value={user?.pin || 'Not provided'} />
                                            <InfoItem
                                                icon={Shield}
                                                label="Role"
                                                value={getRoleDisplay(user?.role)}
                                                badge
                                                badgeColor={getRoleColor(user?.role)}
                                            />
                                            <InfoItem
                                                icon={CheckCircle}
                                                label="Status"
                                                value="Active"
                                                badge
                                                badgeColor="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Account Info */}
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                            <div className="p-6 border-b border-gray-200 dark:border-slate-700">
                                <div className="flex items-center gap-2">
                                    <Settings className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                    <h2 className="font-bold text-gray-800 dark:text-white">Account Info</h2>
                                </div>
                            </div>
                            <div className="p-6 space-y-4">
                                <div className="flex items-start gap-3">
                                    <Calendar className="w-4 h-4 text-gray-400 dark:text-slate-500 mt-0.5" />
                                    <div>
                                        <p className="text-xs text-gray-400 dark:text-slate-500 uppercase tracking-wider">Member Since</p>
                                        <p className="text-sm font-medium text-gray-800 dark:text-white">{memberSince}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Clock className="w-4 h-4 text-gray-400 dark:text-slate-500 mt-0.5" />
                                    <div>
                                        <p className="text-xs text-gray-400 dark:text-slate-500 uppercase tracking-wider">Last Login</p>
                                        <p className="text-sm font-medium text-gray-800 dark:text-white">
                                            {lastLogin.toLocaleDateString()} at {lastLogin.toLocaleTimeString()}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Award className="w-4 h-4 text-gray-400 dark:text-slate-500 mt-0.5" />
                                    <div>
                                        <p className="text-xs text-gray-400 dark:text-slate-500 uppercase tracking-wider">Role Level</p>
                                        <p className="text-sm font-medium text-gray-800 dark:text-white capitalize">
                                            {user?.role?.replace('_', ' ') || 'User'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                            <div className="p-6 border-b border-gray-200 dark:border-slate-700">
                                <div className="flex items-center gap-2">
                                    <Globe className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                    <h2 className="font-bold text-gray-800 dark:text-white">Quick Actions</h2>
                                </div>
                            </div>
                            <div className="p-6 space-y-2">
                                <button
                                    onClick={() => window.location.href = '/dashboard'}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                >
                                    <LayoutDashboard className="w-4 h-4 text-gray-400" />
                                    Dashboard
                                </button>
                                <button
                                    onClick={() => window.location.href = '/policies'}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                >
                                    <FileText className="w-4 h-4 text-gray-400" />
                                    My Policies
                                </button>
                                <button
                                    onClick={() => window.location.href = '/quotes'}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                >
                                    <FileCheck className="w-4 h-4 text-gray-400" />
                                    My Quotes
                                </button>
                                <div className="border-t border-gray-200 dark:border-slate-700 my-2" />
                                <button
                                    onClick={() => {
                                        if (window.confirm('Are you sure you want to logout?')) {
                                            localStorage.removeItem('token');
                                            localStorage.removeItem('user');
                                            window.location.href = '/login';
                                        }
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                >
                                    <LogOut className="w-4 h-4" />
                                    Logout
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ========== SUB-COMPONENTS ==========

function InfoItem({ icon: Icon, label, value, badge, badgeColor }) {
    return (
        <div>
            <p className="text-xs text-gray-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <Icon className="w-3 h-3" />
                {label}
            </p>
            {badge ? (
                <span className={`inline-block mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeColor || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'}`}>
                    {value}
                </span>
            ) : (
                <p className="text-sm font-medium text-gray-800 dark:text-white mt-0.5 capitalize truncate">{value}</p>
            )}
        </div>
    );
}

// Additional icons needed for Quick Actions
function LayoutDashboard({ className }) {
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
    );
}

function FileText({ className }) {
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
    );
}

function FileCheck({ className }) {
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
    );
}